import { clamp } from '@/core/rng';

/**
 * The mortal's presence — a body standing on the ground (2026-08-19, the
 * mortal inhabitation skeleton).
 *
 * `DeityPresence` is an EYE that stands off the land; this is a pair of FEET
 * that stand on it. The two are deliberately separate classes rather than one
 * class with a mode flag: they are different bodies, and almost nothing they
 * do is shared — a god floats, dollies, frames and follows; a mortal walks,
 * runs, looks and jumps. What they share is the contract everything downstream
 * reads — `x y z` (the eye), `yaw pitch` (the gaze) — and `main.ts` hands
 * whichever body is active to the renderer through one accessor, so streaming,
 * LOD, fog, sound and the shadow box follow the mortal without knowing it.
 *
 * ## What this is, and is not, in this pass
 *
 * **Client-side exploration only.** Inhabiting uses a real simulation person
 * for identity and a starting position, and then this body walks about on its
 * own; nothing here writes to `BandSystem`, the `Simulation` or the person. The
 * person goes on living their ordinary AI life and may wander off from under
 * the camera. That is the agreed scope of the skeleton: *inhabit this life's
 * viewpoint*, so the world can be judged from inside it.
 *
 * FUTURE: authoritative mortal inhabitation must replace that person's AI
 * movement rather than running a local camera shell — see `HANDOFF.md`, "the
 * mortal skeleton", for the list of what stands between this and mortal play.
 *
 * No body is drawn, no collision is tested (the mortal walks through trees,
 * roofs, rocks and people), no swimming exists (the sea floor is the sea
 * surface, as it is for the deity; a river is waded along its bed), and the
 * four mortal verbs are a palette that does nothing (`ui/mortalVerbs.ts`).
 *
 * ## The mouse is free, and the hand is the deity's hand
 *
 * No pointer lock (Griffin, 2026-08-19, on the first walk: "no locked mouse
 * please"). The cursor stays free, exactly as it is for the deity: a DRAG
 * turns the head — right looks right, down looks down, the same gesture
 * `DeityPresence` reads — and a CLICK is a choice. `main.ts` gives the mortal
 * the deity's hover-name, choose and card ("we can observe a tree from mortal
 * form, we just can't use god acts on it"); the walk, the run and the jump
 * live here. `wasLookingAround()` is what keeps a drag that ends over a tree
 * from choosing the tree, as it does for the deity.
 *
 * ## The numbers
 *
 * Tuning, not law — judged on foot and revised once already (the first walk
 * read as slow and the hop as a moon jump; walk 4.2→5, run 7.2→9, gravity
 * 12.5→24 under a 6 m/s hop, eye 1.7→1.8). They live here and nowhere else so
 * that judging them again is an edit to one file.
 */

/** Metres from the feet to the eye. Shared by the camera and the tests. */
export const MORTAL_EYE_HEIGHT = 1.8;
/** Metres per second on foot, and with Shift held. */
export const WALK_SPEED = 5.0;
export const RUN_SPEED = 9.0;
/** A hop over the ground: 0.75 m of rise at this gravity, up and down in half a second. */
export const JUMP_VELOCITY = 6.0;
/** m/s². Well over Earth's, as games' gravities are: a hop that comes back down, not a moon jump. */
export const GRAVITY = 24;
/** Radians of look per pixel of mouse DRAG — the deity's drag-turns-the-head hand, on foot. */
export const LOOK_SENS = 0.0040;
/** The gaze may tip this far from the horizon either way (80°). No roll, ever. */
export const PITCH_LIMIT = (80 / 180) * Math.PI;
/** Vertical field of view on foot, degrees. The deity's 46° is restored on ascending. */
export const MORTAL_FOV = 60;
/**
 * The largest drop the feet will STEP down in one frame and stay grounded,
 * metres, plus a share of the ground covered that frame so a slope stays a
 * slope at any frame rate: a hillside is walked, a cliff edge is fallen off.
 * Without this a ledge was a snap — the eye dropped ten metres between two
 * frames — and with a fall it is the same gravity a jump already obeys.
 */
const STEP_DOWN = 0.5, STEP_DOWN_PER_METRE = 1.5;

/** What the keys are asking for. Driven by the listeners when wired; by tests when not. */
export interface MortalInput {
  /** −1..1 along the gaze across the ground (W/S). */
  forward: number;
  /** −1..1 to the gaze's right (D/A). */
  strafe: number;
  /** Shift. */
  run: boolean;
  /** Space, held. One jump per press: it must be let go before it jumps again. */
  jump: boolean;
}

/** Wrap an angle into (−π, π]. */
const wrapAngle = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));

/** The keys this body listens for — and, when it is the active body, swallows. */
const KEYS = new Set(['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright',
                      'shift', ' ']);

export class MortalPresence {
  /* ── the body ───────────────────────────────────────────────────────── */
  /** Where the feet stand, world metres. */
  x = 0; z = 0;
  /** Height of the feet, absolute (sea level = 0). Follows the ground when grounded. */
  feetY = 0;
  /** Gaze. Same convention as the deity: yaw 0 looks toward −z; positive pitch looks up. */
  yaw = 0; pitch = 0;
  /** Vertical velocity while airborne, m/s; 0 on the ground. */
  verticalVelocity = 0;
  grounded = true;
  /** The life whose viewpoint this is — identity and starting place only. */
  personId: string | null = null;

  /**
   * WHO OWNS THE INPUT. False while the player is the deity: the listeners drop
   * every key and every pixel of mouse travel, so a hidden mortal never
   * accumulates velocity, a jump, or a turn of the head. `ui/inhabit.ts` flips
   * it; nothing else should.
   */
  active = false;

  readonly input: MortalInput = { forward: 0, strafe: 0, run: false, jump: false };

  private keys = new Set<string>();
  /** Look awaiting `step`, radians. */
  private pendYaw = 0;
  private pendPitch = 0;
  /** True while Space has been held since the last jump — one hop per press. */
  private jumpLatched = false;
  private wired = false;
  /** The drag in progress — the head being turned — and how far it has gone, in pixels. */
  private drag: { x: number; y: number } | null = null;
  private dragPx = 0;
  private lastDragPx = 0;

  constructor(el?: HTMLElement) {
    if (el) this.wire(el);
  }

  /** The eye: feet plus a standing height. What the camera is put at. */
  get y(): number {
    return this.feetY + MORTAL_EYE_HEIGHT;
  }

  /** True while the pointer is down and has turned the head. The pick holds still for it. */
  get dragging(): boolean {
    return this.drag !== null && this.dragPx > 6;
  }

  /**
   * Did the pointer gesture that just ended turn the head? The click event
   * fires after every drag, and a look round that ends over a tree must not
   * choose the tree — the deity's own rule, for the deity's own hand.
   */
  wasLookingAround(): boolean {
    return this.lastDragPx > 6;
  }

  /** The unit gaze direction. */
  lookDir(): { x: number; y: number; z: number } {
    const cp = Math.cos(this.pitch);
    return { x: -Math.sin(this.yaw) * cp, y: Math.sin(this.pitch), z: -Math.cos(this.yaw) * cp };
  }

  /* ── placing the body ───────────────────────────────────────────────── */

  /**
   * Stand here, on this ground, looking this way, as this life. The gaze
   * opens level: the deity's yaw projected onto the horizon, so the player
   * keeps facing the way their eye was facing.
   */
  seat(x: number, z: number, groundY: number, yaw: number, personId: string | null): void {
    this.x = x; this.z = z;
    this.feetY = Math.max(groundY, 0);
    this.yaw = wrapAngle(yaw);
    this.pitch = 0;
    this.verticalVelocity = 0;
    this.grounded = true;
    this.personId = personId;
    this.pendYaw = 0; this.pendPitch = 0;
    this.clearInput();
  }

  /** Feed look travel, in pixels of drag. Right looks right; up looks up. */
  look(dxPx: number, dyPx: number): void {
    this.pendYaw -= dxPx * LOOK_SENS;
    this.pendPitch -= dyPx * LOOK_SENS;
  }

  /**
   * Drop everything the keys and the hand were doing. Called on every change of
   * input ownership and on window blur, so the player
   * never ascends to find the god walking away on a W that stayed latched.
   */
  clearInput(): void {
    this.keys.clear();
    this.input.forward = 0; this.input.strafe = 0;
    this.input.run = false; this.input.jump = false;
    this.jumpLatched = false;
    this.pendYaw = 0; this.pendPitch = 0;
    this.drag = null; this.dragPx = 0; this.lastDragPx = 0;
  }

  /** Leave the body: no life, no input. The position is kept — it is a record, not a claim. */
  release(): void {
    this.personId = null;
    this.clearInput();
  }

  /* ── one frame ──────────────────────────────────────────────────────── */

  /**
   * One frame. `groundAt` is the same terrain truth everything standing on the
   * land is placed with; the sea floor is the sea surface. Grounded feet follow
   * it exactly — no easing, no lookahead — which is what keeps the eye glued
   * to a hillside; a jump, or walking off a ledge, is the one way off it, and
   * gravity brings the feet back to wherever the ground is when they get there.
   */
  step(dt: number, groundAt: (x: number, z: number) => number): void {
    if (this.wired) this.syncInput();
    const g = (x: number, z: number) => Math.max(groundAt(x, z), 0);

    // the head turns as far as the hand turned it, at once — a drag is the
    // gaze itself and smoothing it reads as a heavy head
    if (this.pendYaw !== 0 || this.pendPitch !== 0) {
      this.yaw = wrapAngle(this.yaw + this.pendYaw);
      this.pitch = clamp(this.pitch + this.pendPitch, -PITCH_LIMIT, PITCH_LIMIT);
      this.pendYaw = 0; this.pendPitch = 0;
    }

    // the walk: relative to yaw alone — pitch never changes where the feet go
    // — normalised so a diagonal is no faster than straight ahead
    const inp = this.input;
    let moved = 0;
    if (inp.forward !== 0 || inp.strafe !== 0) {
      const l = Math.hypot(inp.forward, inp.strafe) || 1;
      const spd = (inp.run ? RUN_SPEED : WALK_SPEED) * dt;
      const f = (inp.forward / l) * spd, s = (inp.strafe / l) * spd;
      // forward is −(sin yaw, cos yaw): where the gaze points across the ground
      const sy = Math.sin(this.yaw), cy = Math.cos(this.yaw);
      this.x += -sy * f + cy * s;
      this.z += -cy * f - sy * s;
      moved = spd;
    }

    const ground = g(this.x, this.z);
    if (this.grounded) {
      if (inp.jump && !this.jumpLatched) {
        // only from the ground, and only once per press
        this.verticalVelocity = JUMP_VELOCITY;
        this.grounded = false;
      } else if (ground < this.feetY - (STEP_DOWN + STEP_DOWN_PER_METRE * moved)) {
        // the ground fell away further than a step: a ledge, and the body
        // leaves it under gravity rather than snapping down its face
        this.verticalVelocity = 0;
        this.grounded = false;
      } else {
        // glued: up a rise, down a slope, the feet ARE the ground
        this.feetY = ground;
      }
    }
    this.jumpLatched = inp.jump;

    if (!this.grounded) {
      this.verticalVelocity -= GRAVITY * dt;
      this.feetY += this.verticalVelocity * dt;
      // the ground here — which may have risen into the jump — ends the flight
      if (this.feetY <= ground) {
        this.feetY = ground;
        this.verticalVelocity = 0;
        this.grounded = true;
      }
    }
  }

  private syncInput(): void {
    const k = this.keys;
    this.input.forward =
      (k.has('w') || k.has('arrowup') ? 1 : 0) - (k.has('s') || k.has('arrowdown') ? 1 : 0);
    this.input.strafe =
      (k.has('d') || k.has('arrowright') ? 1 : 0) - (k.has('a') || k.has('arrowleft') ? 1 : 0);
    this.input.run = k.has('shift');
    this.input.jump = k.has(' ');
  }

  /* ── the mouse: a free cursor, and a drag that turns the head ─────────── */

  private wire(el: HTMLElement): void {
    this.wired = true;
    // any button: a drag turns the head. The deity's listener on this same
    // element prevents the context menu already, and is dormant while this
    // body is the player's.
    el.addEventListener('pointerdown', (e) => {
      if (!this.active) return;
      this.drag = { x: e.clientX, y: e.clientY };
      this.dragPx = 0;
      try { el.setPointerCapture(e.pointerId); } catch { /* released already */ }
      e.preventDefault();
    });
    el.addEventListener('pointermove', (e) => {
      if (!this.active || !this.drag) return;
      const dx = e.clientX - this.drag.x, dy = e.clientY - this.drag.y;
      this.dragPx += Math.abs(dx) + Math.abs(dy);
      this.look(dx, dy);
      this.drag = { x: e.clientX, y: e.clientY };
    });
    const end = () => {
      if (!this.active) return;
      if (this.drag) this.lastDragPx = this.dragPx;
      this.drag = null;
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    addEventListener('keydown', (e) => {
      if (!this.active) return;
      const k = e.key.toLowerCase();
      if (!KEYS.has(k)) return;
      this.keys.add(k);
      e.preventDefault();
    });
    addEventListener('keyup', (e) => {
      if (!this.active) return;
      this.keys.delete(e.key.toLowerCase());
    });
    // alt-tab must not leave a key down and the body walking for ever
    addEventListener('blur', () => this.clearInput());
  }
}
